import { Trans, useLingui } from '@lingui/react/macro';
import { AlertCircle, FolderPlus, LayoutTemplate, Plus, SearchX } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupCard from 'modules/manager/ui/components/media-manager-template-group-card';
import { GROUP_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { TemplateGroupGridSkeleton } from 'modules/manager/ui/components/skeletons';
import useCreateTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-create-template-group-modal';
import useDeleteTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-delete-template-group-modal';
import useRenameTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-rename-template-group-modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDEmptyState from 'ud-ui/components/empty-state';
import { UDPagedListToolbar } from 'ud-ui/components/paged-list';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';

const MediaManagerTemplatesListPage: React.FC = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const [search, setSearch] = useState('');

  const {
    data: groups = [],
    currentData,
    isFetching,
    isError,
    refetch,
  } = useMediaManagerTemplateGroupsQuery({ tournamentId: tournamentId ?? 0 }, { skip: !tournamentId });
  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Медиа менеджер</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  // «Повторить» заперта, пока идёт перезапрос: промис `refetch` — кнопке (IMGM-1561).
  const loadGroups = useCallback(() => refetch(), [refetch]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? groups.filter((group) => group.name.toLowerCase().includes(query)) : groups;
  }, [groups, search]);

  // Состояние — из отфильтрованного массива, как у `UDEntityList`: иначе ветка «ничего не найдено» не покажется.
  const listState = deriveAsyncState({
    itemsCount: visibleGroups.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });

  const existingNames = useMemo(() => groups.map((group) => group.name.trim()), [groups]);
  // The list refetches by the mutations' tags — the modals take no «reload» callback (IMGM-1515).
  const { modal: createModal, open: openCreateModal } = useCreateTemplateGroupModal({
    tournamentId: tournamentId ?? null,
    existingNames,
  });
  const { modal: renameModal, open: openRenameModal } = useRenameTemplateGroupModal({ groups });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateGroupModal();

  const openGroup = useCallback(
    (group: MediaManagerTemplateGroup) => {
      navigate(Pages.MEDIA_MANAGER.builders.group(group.id));
    },
    [navigate],
  );

  const actions = (
    <>
      <UDButton
        variant="secondary"
        className="bg-surfaceCard flex items-center"
        onClick={() => navigate(Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY)}
      >
        <LayoutTemplate size={16} className="mr-2" />
        <Trans>Общие шаблоны</Trans>
      </UDButton>
      <UDButton variant="primary" className="flex items-center" onClick={openCreateModal}>
        <Plus size={16} className="mr-2" />
        <Trans>Создать группу</Trans>
      </UDButton>
    </>
  );

  // Пустая организация ведёт прямо в создание группы — кнопка одна, в самом пустом состоянии, а «Общие
  // шаблоны» — ссылкой в пояснении (NN/g, Carbon: пустота ведёт в ключевую задачу). Поиск появляется,
  // только когда есть что искать; пока список грузится или упал, остаётся ряд кнопок.
  const isEmptyOrganization = listState.status === 'empty' && groups.length === 0;

  return (
    <div className="flex h-full flex-col p-6">
      {groups.length > 0 ? (
        <UDPagedListToolbar search={search} onSearchChange={setSearch} searchPlaceholder={t`Поиск`} actions={actions} />
      ) : (
        !isEmptyOrganization && <div className="mb-4 flex justify-end gap-3">{actions}</div>
      )}

      <UDAsyncContent
        state={listState}
        className="flex-1"
        loading={
          <div className="w-full self-start">
            <TemplateGroupGridSkeleton canWrite />
          </div>
        }
        error={
          <UDEmptyState
            icon={<AlertCircle size={28} />}
            title={<Trans>Не удалось загрузить группы шаблонов</Trans>}
            subtitle={<Trans>Проверьте соединение и попробуйте ещё раз</Trans>}
            action={
              <UDButton variant="secondary" onClick={loadGroups}>
                <Trans>Повторить</Trans>
              </UDButton>
            }
          />
        }
        empty={
          isEmptyOrganization ? (
            <UDEmptyState
              className="max-w-xl"
              icon={<FolderPlus size={28} />}
              title={<Trans>Пока нет групп шаблонов</Trans>}
              subtitle={
                <Trans>
                  Группа собирает шаблоны картинок для турниров: анонсы, результаты, таблицы. Начните с пустой группы или скопируйте
                  готовую из{' '}
                  <Link
                    to={Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY}
                    className="text-inkPrimary font-semibold underline"
                    data-testid="media-manager-empty-library-link"
                  >
                    Общих шаблонов
                  </Link>
                </Trans>
              }
              action={
                <UDButton variant="primary" className="flex items-center" onClick={openCreateModal}>
                  <Plus size={16} className="mr-2" />
                  <Trans>Создать группу</Trans>
                </UDButton>
              }
            />
          ) : (
            <UDEmptyState
              icon={<SearchX size={28} />}
              title={<Trans>Ничего не найдено</Trans>}
              subtitle={<Trans>Измените запрос — поиск идёт по названию группы</Trans>}
            />
          )
        }
      >
        <div className={GROUP_GRID_CLASS}>
          {visibleGroups.map((group) => (
            <MediaManagerTemplateGroupCard
              key={group.id}
              group={group}
              canWrite
              onOpen={openGroup}
              onRename={openRenameModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      </UDAsyncContent>

      {createModal}
      {renameModal}
      {deleteModal}
    </div>
  );
};

export default MediaManagerTemplatesListPage;
